import type {
  FilterFieldDefinition,
  FilterItem,
} from "@components/TableFilters/FiltersPanel/FiltersPanel.types.ts";
import type { MovementServicesFilterPayload } from "@services/movement-services/types.ts";

export const MOVEMENT_SERVICES_FIELDS = Object.freeze({
  ID: "id",
  IMO: "imo",
  VESSEL_NAME: "vesselName",
  VCN: "vcn",
  FROM: "from",
  FROM_AREA_BERTH: "fromAreaBerth",
  TO: "to",
  TO_AREA_BERTH: "toAreaBerth",
  ETA: "eta",
  RTD: "rtd",
  ETS: "ets",
  ATS: "ats",
  ETC: "etc",
  ATC: "atc",
  STATUS: "status",
});

interface MovementStatusMappingEntry {
  label: string;
}

const MOVEMENT_STATUS_MAP: Record<string, MovementStatusMappingEntry> = {
  AWAITING_ARRIVAL_TERMINAL_APPROVAL: { label: "awaiting-arrival-terminal-approval" },
  AWAITING_DEPARTURE_TERMINAL_APPROVAL: { label: "awaiting-departure-terminal-approval" },
  AWAITING_MARINE_DEPARTMENT_APPROVAL: { label: "awaiting-marine-department-approval" },
  REJECTED_BY_DEPARTURE_TERMINAL: { label: "rejected-by-departure-terminal" },
  REJECTED_BY_ARRIVAL_TERMINAL: { label: "rejected-by-arrival-terminal" },
  REJECTED_BY_MARINE_DEPARTMENT: { label: "rejected-by-marine-department" },
  APPROVED: { label: "approved" },
  CLOSED: { label: "closed" },
  CANCELLED_BY_SHIPPING_AGENT: { label: "cancelled-by-shipping-agent" },
  CANCELLED_BY_MARINE_DEPARTMENT: { label: "cancelled-by-marine-department" },
  MOVEMENT_STARTED: { label: "movement-started" },
};

export const movementStatuses = Object.entries(MOVEMENT_STATUS_MAP).map(([value, { label }]) => ({
  value,
  label,
}));

interface SelectOption {
  value: string;
  label: string;
}

const LANG_BASE_HEADER = "movement-services.headerLabels";

export const getMovementServicesFilterFields = ({
  t,
  fromToLocationOptions,
  statusOptions,
}: {
  t: (key: string) => string;
  fromToLocationOptions: SelectOption[];
  statusOptions: SelectOption[];
}): Record<string, FilterFieldDefinition> => ({
  id: {
    label: t(`${LANG_BASE_HEADER}.id`),
    type: "integer",
    minLength: 1,
  },
  imo: {
    label: t(`${LANG_BASE_HEADER}.imo`),
    type: "integer",
    minLength: 3,
  },
  vesselName: {
    label: t(`${LANG_BASE_HEADER}.vesselName`),
    type: "text",
    minLength: 3,
  },
  vcn: {
    label: t(`${LANG_BASE_HEADER}.vcn`),
    type: "integer",
    minLength: 3,
  },
  from: {
    label: t(`${LANG_BASE_HEADER}.from`),
    options: fromToLocationOptions,
    type: "autocomplete",
  },
  to: {
    label: t(`${LANG_BASE_HEADER}.to`),
    options: fromToLocationOptions,
    type: "autocomplete",
  },
  fromAreaBerth: {
    label: t(`${LANG_BASE_HEADER}.fromAreaBerth`),
    type: "text",
    minLength: 3,
  },
  toAreaBerth: {
    label: t(`${LANG_BASE_HEADER}.toAreaBerth`),
    type: "text",
    minLength: 3,
  },
  status: {
    label: t(`${LANG_BASE_HEADER}.status`),
    options: statusOptions,
    type: "autocomplete",
  },
  eta: {
    label: t(`${LANG_BASE_HEADER}.eta`),
    type: "dateFromTo",
  },
  rtd: {
    label: t(`${LANG_BASE_HEADER}.rtd`),
    type: "dateFromTo",
  },
  ets: {
    label: t(`${LANG_BASE_HEADER}.ets`),
    type: "dateFromTo",
  },
  ats: {
    label: t(`${LANG_BASE_HEADER}.ats`),
    type: "dateFromTo",
  },
  etc: {
    label: t(`${LANG_BASE_HEADER}.etc`),
    type: "dateFromTo",
  },
  atc: {
    label: t(`${LANG_BASE_HEADER}.atc`),
    type: "dateFromTo",
  },
});

const toIsoDateOrNull = (value: string) => {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d.toISOString();
};

const toMovementIdFilterValue = (value: string) => {
  const trimmedValue = value.trim();
  return /^\d+$/.test(trimmedValue) ? Number(trimmedValue) : trimmedValue;
};

type MovementArrayFilterField =
  | "imo"
  | "vesselName"
  | "vesselCallNo"
  | "from"
  | "fromAreaBerth"
  | "toAreaBerth"
  | "to"
  | "status";

type MovementDateFilterField =
  | "etaFrom"
  | "etaTo"
  | "rtdFrom"
  | "rtdTo"
  | "etsFrom"
  | "etsTo"
  | "atsFrom"
  | "atsTo"
  | "etcFrom"
  | "etcTo"
  | "atcFrom"
  | "atcTo";

const appendIdFilterValue = (body: MovementServicesFilterPayload, value: number | string) => {
  const currentValues = body.ids ?? [];

  if (currentValues.includes(value)) {
    return;
  }

  body.ids = [...currentValues, value];
};

const appendFilterValue = (
  body: MovementServicesFilterPayload,
  field: MovementArrayFilterField,
  value: string,
) => {
  const currentValues = body[field] ?? [];

  if (currentValues.includes(value)) {
    return;
  }

  body[field] = [...currentValues, value];
};

const appendDateFilterValue = (
  body: MovementServicesFilterPayload,
  field: MovementDateFilterField,
  value: string | null,
) => {
  const currentValue = body[field];
  const currentValues = Array.isArray(currentValue) ? currentValue : [];

  body[field] = [...currentValues, value] as MovementServicesFilterPayload[typeof field];
};

export const buildFilterBodyForMovements = (
  filters: FilterItem[],
): MovementServicesFilterPayload => {
  const body: MovementServicesFilterPayload = {};

  for (const f of filters) {
    if (!f.field || f.value === "" || f.value == null) continue;

    switch (f.field) {
      case "id": {
        const value = String(f.value).trim();
        if (!value) break;

        appendIdFilterValue(body, toMovementIdFilterValue(value));
        break;
      }
      case "imo":
      case "vesselName":
      case "from":
      case "to":
      case "fromAreaBerth":
      case "toAreaBerth":
      case "status": {
        const value = String(f.value).trim();
        if (!value) break;

        appendFilterValue(body, f.field, value);
        break;
      }
      case "vcn": {
        const value = String(f.value).trim();
        if (!value) break;

        appendFilterValue(body, "vesselCallNo", value);
        break;
      }
      case "eta":
      case "rtd":
      case "ets":
      case "ats":
      case "etc":
      case "atc": {
        const valueAsRange = f.value as { from?: string; to?: string };

        appendDateFilterValue(
          body,
          `${f.field}From` as MovementDateFilterField,
          toIsoDateOrNull(valueAsRange.from ?? ""),
        );
        appendDateFilterValue(
          body,
          `${f.field}To` as MovementDateFilterField,
          toIsoDateOrNull(valueAsRange.to ?? ""),
        );
        break;
      }

      default:
        break;
    }
  }

  return body;
};
